/**
 * Resimlerde ortak kullanılan parçalar. Figürler bilerek yüzsüzdür (göz, ağız
 * çizilmez); kıyafetler sade ve örtülüdür. Kitap sayfalarında yazı yoktur,
 * yalnız ince çizgiler vardır.
 */

export const PALETTE = {
  deep: "#006A71",
  brand: "#48A6A7",
  soft: "#8FC9CA",
  mist: "#D6EBEB",
  cloud: "#F1F8F8",
  ink: "#123B3D",
  orange: "#E8862E",
  gold: "#E8A23A",
  skin: "#E8C4A0",
  skinShade: "#D9AE86",
  wood: "#B87436",
  woodDark: "#8E5626",
  paper: "#FFFDF7",
};

/** Yerdeki yumuşak gölge. */
export function Shadow({ cx, cy, rx, ry = rx * 0.14, opacity = 0.12 }) {
  return <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill={PALETTE.ink} opacity={opacity} />;
}

/** Hilal ve küçük yıldızlar, arka plan süsü. */
export function Crescent({ x, y, r = 14, color = PALETTE.gold, bg = PALETTE.cloud }) {
  return (
    <g>
      <circle cx={x} cy={y} r={r} fill={color} />
      <circle cx={x + r * 0.42} cy={y - r * 0.22} r={r * 0.86} fill={bg} />
    </g>
  );
}

export function Star({ x, y, r = 4, color = PALETTE.gold, opacity = 1 }) {
  const s = r * 0.32;
  return (
    <path
      d={`M${x},${y - r} Q${x + s},${y - s} ${x + r},${y} Q${x + s},${y + s} ${x},${y + r} Q${x - s},${y + s} ${x - r},${y} Q${x - s},${y - s} ${x},${y - r} Z`}
      fill={color}
      opacity={opacity}
    />
  );
}

/** Kubbe ve minareli sade cami silüeti; (x, y) zemin ortası. */
export function MosqueSilhouette({ x, y, w = 160, color = PALETTE.mist, opacity = 1 }) {
  const k = w / 160;
  return (
    <g transform={`translate(${x} ${y}) scale(${k})`} fill={color} opacity={opacity}>
      <rect x={-50} y={-40} width={100} height={40} rx={3} />
      <path d="M-34,-40 Q-34,-86 0,-92 Q34,-86 34,-40 Z" />
      <rect x={-1.5} y={-104} width={3} height={14} rx={1.5} />
      <circle cx={0} cy={-106} r={3.5} />
      <rect x={-74} y={-100} width={9} height={100} rx={2} />
      <path d="M-74,-100 L-69.5,-116 L-65,-100 Z" />
      <rect x={65} y={-100} width={9} height={100} rx={2} />
      <path d="M65,-100 L69.5,-116 L74,-100 Z" />
    </g>
  );
}

/**
 * Bağdaş kurup oturan yüzsüz figür, önden. (x, y) bacakların alt ortası;
 * yükseklik yaklaşık 115 birim × scale.
 * headwear: "takke" | "hijab"
 * arms: "read" (eller önde, kitaba) | "rest" (eller dizlerde)
 */
export function SeatedFigure({
  x,
  y,
  scale = 1,
  robe = PALETTE.deep,
  robeDark = "#0B565B",
  headwear = "takke",
  scarf = PALETTE.brand,
  cap = "#FFFFFF",
  arms = "read",
  back = false,
}) {
  const { skin, skinShade } = PALETTE;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      {/* bacaklar */}
      <path d="M-50,0 Q-54,-24 -22,-27 L22,-27 Q54,-24 50,0 Q0,9 -50,0 Z" fill={robeDark} />
      {/* gövde (cübbe) */}
      <path d="M-27,-20 L-31,-62 Q-31,-77 -14,-79 L14,-79 Q31,-77 31,-62 L27,-20 Q0,-14 -27,-20 Z" fill={robe} />
      {/* boyun */}
      <rect x={-5.5} y={-86} width={11} height={9} rx={3} fill={skinShade} />
      {headwear === "hijab" ? (
        <>
          {/* başörtüsü omuzlara iner */}
          <path
            d="M0,-117 C-20,-117 -23,-99 -20,-87 L-29,-70 Q0,-60 29,-70 L20,-87 C23,-99 20,-117 0,-117 Z"
            fill={scarf}
          />
          {back ? null : <ellipse cx={0} cy={-96} rx={10} ry={12} fill={skin} />}
        </>
      ) : (
        <>
          <circle cx={0} cy={-98} r={15} fill={back ? skinShade : skin} />
          {/* takke */}
          <path d="M-14.5,-102 Q-14,-117 0,-117 Q14,-117 14.5,-102 Q0,-99 -14.5,-102 Z" fill={cap} />
          <path d="M-14,-103.5 Q0,-100.5 14,-103.5" stroke={PALETTE.mist} strokeWidth={1.6} fill="none" />
        </>
      )}
      {/* kollar */}
      {back ? (
        <>
          <path d="M-27,-68 Q-36,-44 -30,-26" stroke={robe} strokeWidth={11} strokeLinecap="round" fill="none" />
          <path d="M27,-68 Q36,-44 30,-26" stroke={robe} strokeWidth={11} strokeLinecap="round" fill="none" />
        </>
      ) : arms === "read" ? (
        <>
          <path d="M-25,-68 Q-34,-44 -15,-40" stroke={robe} strokeWidth={11} strokeLinecap="round" fill="none" />
          <path d="M25,-68 Q34,-44 15,-40" stroke={robe} strokeWidth={11} strokeLinecap="round" fill="none" />
          <circle cx={-13} cy={-40} r={5} fill={skin} />
          <circle cx={13} cy={-40} r={5} fill={skin} />
        </>
      ) : (
        <>
          <path d="M-25,-68 Q-36,-42 -30,-24" stroke={robe} strokeWidth={11} strokeLinecap="round" fill="none" />
          <path d="M25,-68 Q36,-42 30,-24" stroke={robe} strokeWidth={11} strokeLinecap="round" fill="none" />
          <circle cx={-30} cy={-22} r={5} fill={skin} />
          <circle cx={30} cy={-22} r={5} fill={skin} />
        </>
      )}
    </g>
  );
}

/** Açık kitap, önden hafif eğik. (x, y) cilt ortasının altı. */
export function OpenBook({ x, y, w = 60, cover = PALETTE.deep, tilt = 0 }) {
  const k = w / 60;
  return (
    <g transform={`translate(${x} ${y}) rotate(${tilt}) scale(${k})`}>
      <path d="M-31,-2 L-29,-22 Q-14,-26 0,-20 Q14,-26 29,-22 L31,-2 Q14,-6 0,1 Q-14,-6 -31,-2 Z" fill={cover} />
      <path d="M-28,-5 L-27,-23 Q-13,-27 0,-21 L0,-2 Q-13,-8 -28,-5 Z" fill={PALETTE.paper} />
      <path d="M28,-5 L27,-23 Q13,-27 0,-21 L0,-2 Q13,-8 28,-5 Z" fill="#F4EFE3" />
      <g stroke={PALETTE.soft} strokeWidth={1.1} strokeLinecap="round" opacity={0.9}>
        <path d="M-23,-19 Q-13,-21 -5,-17.5" fill="none" />
        <path d="M-23,-15 Q-13,-17 -5,-13.5" fill="none" />
        <path d="M-23,-11 Q-13,-13 -5,-9.5" fill="none" />
        <path d="M5,-17.5 Q13,-21 23,-19" fill="none" />
        <path d="M5,-13.5 Q13,-17 23,-15" fill="none" />
        <path d="M5,-9.5 Q13,-13 19,-11.5" fill="none" />
      </g>
      <path d="M0,-21 L0,-1" stroke={PALETTE.skinShade} strokeWidth={0.8} opacity={0.6} />
    </g>
  );
}

/** Elde tutulan açık kitap, sırtı bize dönük (okuyan karşıda). */
export function HeldBook({ x, y, w = 64, cover = PALETTE.orange, coverDark = "#C96F1E", accent = PALETTE.paper }) {
  const k = w / 64;
  return (
    <g transform={`translate(${x} ${y}) scale(${k})`}>
      <path d="M-32,-14 Q-16,-11 0,-6 Q16,-11 32,-14 L31,-17 Q16,-14 0,-9 Q-16,-14 -31,-17 Z" fill={PALETTE.paper} />
      <path d="M-33,-12 Q-16,-9 0,-4 L0,18 Q-16,12 -30,11 Z" fill={cover} />
      <path d="M33,-12 Q16,-9 0,-4 L0,18 Q16,12 30,11 Z" fill={coverDark} />
      <path d="M-24,-3 Q-14,-1 -6,2 M-24,3 Q-14,5 -6,8" stroke={accent} strokeWidth={1.4} strokeLinecap="round" fill="none" opacity={0.7} />
    </g>
  );
}
